import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

// The lines the operator is putting back. DELIV_ITEM is fixed here: every row was started
// from a line the payload already had, so the number is the one SAP showed us. A line with
// several lots gets one row per lot, so the lot it came from is what names the row.
export default function AddedLines({ lines, onChange, onRemove, isUnpaired, disabled }) {
  const { t } = useTranslation()
  const key = suffix => `natura_operations.picking.${suffix}`

  return (
    <section className="mt-6">
      <h2 className="font-semibold mb-1">{t(key('added_heading'))}</h2>
      <p className="text-sm text-gray-600 mb-3">{t(key('added_explanation'))}</p>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">{t(key('column_deliv_item'))}</th>
              <th className="px-3 py-2 text-left font-semibold">{t(key('column_sku'))}</th>
              <th className="px-3 py-2 text-left font-semibold">{t(key('column_batch'))}</th>
              <th className="px-3 py-2 text-right font-semibold">{t(key('column_quantity'))}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {lines.map(line => (
              <tr key={line.id} className={`border-t border-gray-200 ${isUnpaired(line) ? 'bg-red-50' : ''}`}>
                <td className="px-3 py-2 font-mono align-middle">
                  {line.deliv_item}
                  {isUnpaired(line) && <span className="ml-2 text-xs text-amber-800 font-sans">{t(key('unpaired_badge'))}</span>}
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={line.sku}
                    disabled={disabled}
                    onChange={event => onChange(line.id, { sku: event.target.value })}
                    aria-label={t(key('added_sku_aria'), { line: line.deliv_item, source: line.source_batch })}
                    className="w-40 px-2 py-1 border border-gray-300 rounded font-mono disabled:bg-gray-100"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={line.batch}
                    disabled={disabled}
                    onChange={event => onChange(line.id, { batch: event.target.value })}
                    aria-label={t(key('added_batch_aria'), { line: line.deliv_item, source: line.source_batch })}
                    className="w-32 px-2 py-1 border border-gray-300 rounded font-mono disabled:bg-gray-100"
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={line.dlv_qty}
                    disabled={disabled}
                    onChange={event => onChange(line.id, { dlv_qty: event.target.value })}
                    aria-label={t(key('added_quantity_aria'), { line: line.deliv_item, source: line.source_batch })}
                    className="w-20 px-2 py-1 border border-gray-300 rounded font-mono text-right disabled:bg-gray-100"
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => onRemove(line.id)}
                    disabled={disabled}
                    className="px-2 py-1 text-sm rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                  >
                    {t(key('added_discard'))}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Natura only accepts a lot it already received via WP10 and rejects it at invoicing
          even after a 200 here, so the operator has to know before sending, not after. */}
      <p className="mt-2 text-sm text-amber-700">{t(key('added_lot_caution'))}</p>
    </section>
  )
}

AddedLines.propTypes = {
  lines: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  isUnpaired: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}

AddedLines.defaultProps = {
  disabled: false,
}
